import { ClientTransferRequestId, daysAgo } from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { Spinner } from "components/ui/spinner";
import * as React from "react";
import { navigate } from "store/actions";
import { useRegionId } from "store/selectors/hooks";
import { TransferSubjectHeading } from "./transfer-subject-heading";
import { ClientTransferRequestSummary } from "./transfer-types";

interface Props {
    clientTransferRequest: ClientTransferRequestSummary;
    onRejected: (clientTransferRequestId: ClientTransferRequestId) => void;
}

export const IncomingTransferCard: React.FunctionComponent<Props> = (props: Props) => {
    const { clientTransferRequest, onRejected } = props;
    const regionId = useRegionId();
    const trpcContext = trpc.useTRPC();

    const acceptTransfer = useMutation(
        trpcContext.clientTransfer.accept.mutationOptions(),
    );
    const rejectTransfer = useMutation(
        trpcContext.clientTransfer.reject.mutationOptions(),
    );

    const onAcceptTransfer = React.useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            const accepted = await acceptTransfer.mutateAsync({
                clientTransferRequestId: clientTransferRequest.id,
                regionId: regionId,
            });
            navigate(accepted.appLink);
        },
        [acceptTransfer, clientTransferRequest.id, regionId],
    );

    const onRejectTransfer = React.useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            const confirm = await fancyConfirm(
                "Decline transfer?",
                "This will decline the transfer request. The client will not be transferred.",
                "Yes, decline",
                "No, go back",
            );

            if (!confirm) {
                return;
            }
            await rejectTransfer.mutateAsync({
                clientTransferRequestId: clientTransferRequest.id,
            });
            onRejected(clientTransferRequest.id);
        },
        [rejectTransfer, clientTransferRequest.id, onRejected],
    );

    return (
        <div className="col-span-1 divide-y divide-gray-200 rounded md:rounded-lg bg-white shadow">
            <div className="flex-1 overflow-auto p-4">
                <h3 className="twoverride text-gray-900 text-lg">
                    <TransferSubjectHeading
                        clientTransferRequest={clientTransferRequest}
                    />
                </h3>
            </div>
            <div className="flex-1 overflow-auto">
                <ul className="p-4 space-y-2">
                    <li>Sent by: {clientTransferRequest.sentByName}</li>
                    <li>To: {clientTransferRequest.sentToName}</li>
                    <li>Sent {daysAgo(clientTransferRequest.dateAdded)}</li>
                </ul>
            </div>
            <div className="-mt-px flex divide-x divide-gray-200 cursor-pointer">
                <div className="flex w-0 flex-1">
                    <a
                        onClick={onAcceptTransfer}
                        className="relative inline-flex w-0 flex-1 items-center justify-center border border-transparent py-4 text-sm font-medium text-green-600 bg-green-100 hover:bg-gray-100 hover:text-gray-800 twoverride rounded-bl-lg">
                        <span className="ml-3">Accept transfer</span>
                        {acceptTransfer.isPending ? <Spinner /> : null}
                    </a>
                    <a
                        onClick={onRejectTransfer}
                        className="relative inline-flex w-0 flex-1 items-center justify-center border border-transparent py-4 text-sm font-medium text-red-600 bg-red-100 hover:bg-gray-100 hover:text-gray-800 twoverride rounded-br-lg">
                        <span className="ml-3">Decline transfer</span>
                        {rejectTransfer.isPending ? <Spinner /> : null}
                    </a>
                </div>
            </div>
        </div>
    );
};
